import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ChangeEvent, DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api/client.js';
import { useT } from '../state/i18n.js';

// 자료함(폴더) 문서 패널 — 목록·업로드·제목변경·휴지통(소프트 삭제/복구)·열기·공유 링크 관리.
// 권한 축은 문서함 역할(myRole): VIEWER 는 읽기 전용, EDITOR+ 는 쓰기.
// 최종 판정은 항상 서버 — 여기 disabled 는 UX 힌트일 뿐이다(서버가 403 을 주면 그 메시지를 그대로 노출).
//
// 자료실 프리미티브: 자료함 + 같은 식별자로 묶인 자료 + 접근권한.
// 업로드는 절대 공유 링크를 만들지 않는다(linkMode:'none') — 올린 문서는 내부 전용이고 자료함 ACL 이 통치한다.
// 무인증 공개 URL 은 사람이 문서별로 명시해서 부여하는 권한이며, 그 부여·중지·재개 지점이 아래 "공유" 패널이다.

type DocKind = 'html' | 'md' | 'pdf' | 'pptx';

interface DocRow {
  id: string;
  title: string;
  kind: DocKind;
  fileSize: number;
  createdAt: string;
  shareUrl: string | null;
}

/** 공유 패널이 다루는 링크 한 건 — 열람 분석 EP(summary.links)에서 온다. */
interface ShareLinkRow {
  id: string;
  alias: string;
  expiresAt: string | null;
  disabled: boolean;
  allowDownload: boolean;
  shareUrl: string;
}

/** 열린 공유 패널의 링크 상태 — 패널은 한 번에 하나만 열리므로 단일 슬롯. */
interface SharePanelState {
  docId: string;
  loading: boolean;
  error: string | null;
  links: ShareLinkRow[];
}

const KIND_LABEL: Record<DocKind, string> = { html: 'HTML', md: 'MD', pdf: 'PDF', pptx: 'PPTX' };
// KIND_LABEL 값은 언어 무관 포맷 명(HTML/MD/PDF/PPTX)이라 t() 로 감싸지 않는다.

/** 서버 multer 한도와 같은 값 — 서버도 막지만 큰 파일을 헛되이 올리지 않도록 미리 차단. */
const MAX_BYTES = 50 * 1024 * 1024;
const ACCEPT = '.html,.htm,.pdf,.md,.markdown,.pptx';
const ALLOWED_EXT = /\.(html?|md|markdown|pdf|pptx)$/i;

function stripExt(name: string): string {
  return name.replace(ALLOWED_EXT, '').trim() || name;
}

function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

function formatDate(value: string): string {
  return String(value ?? '').slice(0, 10);
}

/** ISO → <input type="date"> 값. null(무만료)은 빈 값. */
function toDateInput(value: string | null): string {
  return value ? String(value).slice(0, 10) : '';
}

function messageOf(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}

/** POST /api/proposals/:id/restore — client.ts(W1 소유)에 아직 메서드가 없어 임시 로컬 호출.
 *  요청·에러 규약은 client.request 와 동일({ok:false,error} → ApiError). W1 이
 *  api.restoreProposal 을 추가하면 이 함수를 지우고 그걸로 교체할 것. */
async function restoreProposalReq(proposalId: string): Promise<void> {
  const response = await fetch(`/api/proposals/${encodeURIComponent(proposalId)}/restore`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  const data = (await response.json().catch(() => null)) as { error?: string } | null;
  if (!response.ok) throw new ApiError(response.status, data?.error || response.statusText);
}

export function FolderDocs({
  projectId,
  projectCode,
  myRole,
  onError,
}: {
  projectId: string;
  projectCode: string;
  myRole: 'ADMIN' | 'EDITOR' | 'VIEWER';
  onError?: (message: string) => void;
}): JSX.Element {
  const t = useT();
  const canEdit = myRole === 'ADMIN' || myRole === 'EDITOR';

  const [docs, setDocs] = useState<DocRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [upload, setUpload] = useState<{ done: number; total: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  // 공유 패널이 열린 문서 / 그 패널의 링크 상태 / 방금 복사한 링크
  const [shareId, setShareId] = useState<string | null>(null);
  const [panel, setPanel] = useState<SharePanelState | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [linkBusyId, setLinkBusyId] = useState<string | null>(null);
  // 만료일 입력 초안 — 링크별. '적용' 전에는 서버로 아무것도 보내지 않는다.
  const [expiryDraft, setExpiryDraft] = useState<Record<string, string>>({});
  // 휴지통 — 서버가 삭제 문서 목록 조회를 아직 안 준다(목록 EP 는 deletedAt IS NULL 고정).
  // 그래서 이 목록은 "이번 화면에서 옮긴 문서"만 담는다 — 복구 자체는 서버 EP 로 언제나 실행된다.
  const [trash, setTrash] = useState<DocRow[]>([]);
  const [trashOpen, setTrashOpen] = useState(false);

  const fileRef = useRef<HTMLInputElement | null>(null);
  // onError 는 부모가 인라인 화살표로 넘길 수 있다 — deps 에 넣으면 매 렌더 재조회가 돈다. ref 로 고정.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  // projectId 가 바뀌는 동안 먼저 뜬 응답이 나중 응답을 덮지 않도록.
  const reqRef = useRef(0);

  const fail = useCallback((message: string) => {
    setError(message);
    onErrorRef.current?.(message);
  }, []);

  const load = useCallback(async () => {
    const seq = ++reqRef.current;
    setLoading(true);
    try {
      const res = await api.folderDocs(projectId);
      if (seq !== reqRef.current) return;
      setDocs(
        res.proposals.map((p) => ({
          id: p.id,
          title: p.title,
          kind: p.kind,
          fileSize: p.fileSize,
          createdAt: String(p.createdAt),
          shareUrl: p.shareUrl,
        })),
      );
    } catch (err) {
      if (seq !== reqRef.current) return;
      fail(messageOf(err, t({ ko: '문서 목록을 불러오지 못했습니다.', en: 'Could not load documents.' })));
    } finally {
      if (seq === reqRef.current) setLoading(false);
    }
  }, [projectId, fail, t]);

  /** 목록 EP 는 링크 상태(중지·만료·다운로드)를 주지 않는다 — 패널을 열 때만 열람 분석 EP 로
   *  전체 링크(중지 포함)를 지연 조회한다. 중지된 링크는 목록 shareUrl 에서 빠지므로(활성 1건 규칙)
   *  여기서만 보인다 — '중지됨' 구분 표시가 이 패널의 몫인 이유. */
  const loadLinks = useCallback(async (docId: string) => {
    setPanel({ docId, loading: true, error: null, links: [] });
    try {
      const res = await api.proposalSummary(docId, null);
      setPanel((cur) =>
        cur?.docId === docId
          ? {
              docId,
              loading: false,
              error: null,
              links: res.summary.links.map((l) => ({
                id: l.id,
                alias: l.alias,
                expiresAt: l.expiresAt,
                disabled: l.disabled,
                allowDownload: l.allowDownload,
                shareUrl: l.shareUrl,
              })),
            }
          : cur,
      );
    } catch (err) {
      setPanel((cur) =>
        cur?.docId === docId
          ? {
              docId,
              loading: false,
              error: messageOf(err, t({ ko: '링크 상태를 불러오지 못했습니다.', en: 'Could not load link status.' })),
              links: [],
            }
          : cur,
      );
    }
  }, [t]);

  useEffect(() => {
    setEditingId(null);
    setShareId(null);
    setPanel(null);
    setCopiedId(null);
    setExpiryDraft({});
    setTrash([]); // 휴지통은 폴더 단위 — 다른 폴더의 삭제분을 이월하지 않는다
    setTrashOpen(false);
    setError(null);
    void load();
  }, [load]);

  // ── 업로드 ──────────────────────────────────────────────────────────────
  async function uploadFiles(files: File[]): Promise<void> {
    if (!canEdit || files.length === 0) return;
    const problems: string[] = [];
    const queue: File[] = [];
    for (const file of files) {
      if (!ALLOWED_EXT.test(file.name)) {
        problems.push(
          t({
            ko: `${file.name}: 지원하지 않는 형식입니다 (HTML·MD·PDF·PPTX)`,
            en: `${file.name}: unsupported format (HTML, MD, PDF, PPTX)`,
          }),
        );
      } else if (file.size > MAX_BYTES) {
        problems.push(
          t({
            ko: `${file.name}: 50MB를 넘습니다 (${formatSize(file.size)})`,
            en: `${file.name}: exceeds 50MB (${formatSize(file.size)})`,
          }),
        );
      } else {
        queue.push(file);
      }
    }

    setError(null);
    if (queue.length > 0) {
      setUpload({ done: 0, total: queue.length });
      let done = 0;
      for (const file of queue) {
        setUpload({ done, total: queue.length });
        done += 1;
        const form = new FormData();
        form.append('pdf', file); // 서버 필드명은 'pdf'(레거시) — html/md/pdf/pptx 다 받는다
        form.append('title', stripExt(file.name));
        form.append('projectCode', projectCode);
        // 내부 전용 업로드 — 서버 기본값이 '링크 생성'이므로 반드시 명시해야 한다.
        // accessMode 는 링크의 속성이라 링크가 없는 여기서는 보내지 않는다(보내면 의미 없는 신호).
        form.append('linkMode', 'none');
        try {
          await api.uploadProposal(form);
        } catch (err) {
          problems.push(`${file.name}: ${messageOf(err, t({ ko: '업로드에 실패했습니다.', en: 'Upload failed.' }))}`);
        }
      }
      setUpload(null);
      await load(); // 일부 실패해도 성공분은 반영되므로 무조건 재조회
    }

    if (problems.length > 0) fail(problems.join('\n'));
  }

  function onPick(event: ChangeEvent<HTMLInputElement>): void {
    const files = Array.from(event.target.files ?? []);
    event.target.value = ''; // 같은 파일 재선택 허용
    void uploadFiles(files);
  }

  function onDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setDragging(false);
    if (!canEdit) return;
    void uploadFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  function onDragOver(event: DragEvent<HTMLDivElement>): void {
    if (!canEdit) return;
    event.preventDefault();
    setDragging(true);
  }

  function onDragLeave(event: DragEvent<HTMLDivElement>): void {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    setDragging(false);
  }

  // ── 문서 조작 ────────────────────────────────────────────────────────────
  function openDoc(doc: DocRow): void {
    window.open(api.folderDocContentUrl(projectId, doc.id), '_blank', 'noopener,noreferrer');
  }

  function startRename(doc: DocRow): void {
    setEditingId(doc.id);
    setEditTitle(doc.title);
  }

  async function saveTitle(doc: DocRow): Promise<void> {
    const next = editTitle.trim();
    if (!next || next === doc.title) {
      setEditingId(null);
      return;
    }
    setBusyId(doc.id);
    setError(null);
    try {
      await api.updateProposal(doc.id, null, next);
      setEditingId(null);
      await load();
    } catch (err) {
      fail(messageOf(err, t({ ko: '제목을 변경하지 못했습니다.', en: 'Could not rename the document.' })));
    } finally {
      setBusyId(null);
    }
  }

  // ── 공유 링크(문서별 권한 부여·중지·재개) ────────────────────────────────
  function toggleShare(doc: DocRow): void {
    setCopiedId(null);
    const next = shareId === doc.id ? null : doc.id;
    setShareId(next);
    if (next) void loadLinks(doc.id);
    else setPanel(null);
  }

  /** 무인증 공개 URL 을 이 문서에만 부여한다. 성공 판정은 서버 응답 뒤 재조회로만 한다. */
  async function createShareLink(doc: DocRow): Promise<void> {
    if (!canEdit) return;
    if (
      !window.confirm(
        t({
          ko: `"${doc.title}" 문서의 공유 링크를 만들까요?\n\n링크를 아는 사람은 로그인 없이 열람합니다.`,
          en: `Create a share link for "${doc.title}"?\n\nAnyone with the link can view it without signing in.`,
        }),
      )
    )
      return;
    setBusyId(doc.id);
    setError(null);
    try {
      await api.createLink(doc.id, null, { accessMode: 'public' });
      await load();
      await loadLinks(doc.id); // 패널도 새 링크를 바로 보여준다
    } catch (err) {
      // 서버가 소유자/org ADMIN 만 허용해 403·404 를 줄 수 있다 — 그 메시지를 그대로 보여준다.
      fail(messageOf(err, t({ ko: '공유 링크를 만들지 못했습니다.', en: 'Could not create the share link.' })));
    } finally {
      setBusyId(null);
    }
  }

  /** 부분 PATCH — 바꾸는 필드만 보낸다. 다른 필드를 같이 실으면 서버의 '안 보낸 필드 무변경'
   *  계약이 무의미해진다(disabled 리셋 류 사고 방지의 핵심). ownerKey 인자는 ''(빈 값) 고정 —
   *  org 문서는 서버가 ownerKey 축을 인정하지 않고 세션으로만 판정하며, 실제 ownerKey 는 항상
   *  비어있지 않아 빈 값이 오인될 표면도 없다(client.ts 시그니처가 string 필수 — W1 소유). */
  async function patchLink(doc: DocRow, link: ShareLinkRow, patch: Record<string, unknown>): Promise<boolean> {
    setLinkBusyId(link.id);
    setError(null);
    try {
      const res = await api.updateLink(doc.id, link.id, '', patch);
      // 서버가 최종 값(만료 상한 캡 등)을 돌려준다 — 응답으로 갱신해야 캡 결과가 그대로 보인다.
      setPanel((cur) =>
        cur && cur.docId === doc.id
          ? {
              ...cur,
              links: cur.links.map((l) =>
                l.id === link.id
                  ? {
                      ...l,
                      alias: res.link.alias,
                      expiresAt: res.link.expiresAt,
                      disabled: res.link.disabled,
                      allowDownload: res.link.allowDownload,
                    }
                  : l,
              ),
            }
          : cur,
      );
      await load(); // 대표 링크 활성 여부('공유중' 필)가 바뀔 수 있어 목록도 재조회
      return true;
    } catch (err) {
      // 링크 관리는 소유자/org ADMIN 만 — 폴더 EDITOR 비소유자는 403 을 받을 수 있다. 메시지 그대로.
      fail(messageOf(err, t({ ko: '링크를 수정하지 못했습니다.', en: 'Could not update the link.' })));
      return false;
    } finally {
      setLinkBusyId(null);
    }
  }

  /** 중지는 확인 없이 즉시 — 잘못 보낸 링크를 끄는 응급 동선이고, 삭제와 달리 통계가 남고 재개도
   *  된다. 재개는 외부 노출을 다시 여는 권한 부여라 확인을 받는다(생성과 같은 등급). */
  async function toggleLinkDisabled(doc: DocRow, link: ShareLinkRow): Promise<void> {
    if (
      link.disabled &&
      !window.confirm(
        t({
          ko: `링크 "${link.alias}"을(를) 다시 활성화할까요?\n\n링크를 아는 사람은 로그인 없이 열람합니다.`,
          en: `Re-enable the link "${link.alias}"?\n\nAnyone with the link can view it without signing in.`,
        }),
      )
    )
      return;
    await patchLink(doc, link, { disabled: !link.disabled });
  }

  /** 만료일 적용 — 빈 값 = 무만료 '요청'. 실제 무만료는 상한 없는 플랜에서만 되고, 하위 플랜은
   *  서버가 상한으로 캡한다. 성공한 척하지 않도록 표시는 항상 서버 응답 값을 쓴다(patchLink). */
  async function applyExpiry(doc: DocRow, link: ShareLinkRow): Promise<void> {
    const raw = expiryDraft[link.id];
    if (raw === undefined) return; // 초안 없음 = 무변경
    const expiresAt = raw === '' ? null : new Date(`${raw}T23:59:59`).toISOString();
    const ok = await patchLink(doc, link, { expiresAt });
    if (ok) {
      setExpiryDraft((cur) => {
        const next = { ...cur };
        delete next[link.id];
        return next;
      });
    }
  }

  async function copyLinkUrl(link: ShareLinkRow): Promise<void> {
    try {
      await navigator.clipboard.writeText(link.shareUrl);
      setCopiedId(link.id);
    } catch {
      fail(
        t({
          ko: '클립보드에 복사하지 못했습니다 — 주소를 직접 선택해 복사하세요.',
          en: 'Could not copy to clipboard — select the address and copy it manually.',
        }),
      );
    }
  }

  // ── 휴지통(소프트 삭제/복구) ─────────────────────────────────────────────
  async function removeDoc(doc: DocRow): Promise<void> {
    if (
      !window.confirm(
        t({
          ko: `"${doc.title}" 문서를 휴지통으로 이동할까요?\n\n발행된 공유 링크는 즉시 모두 중지됩니다.\n복구는 언제든 가능하지만, 복구해도 공유 링크는 중지 상태로 남습니다(재공유는 명시적으로).`,
          en: `Move "${doc.title}" to trash?\n\nAny published share links are stopped immediately.\nYou can restore it anytime, but restoring keeps the share links stopped (re-sharing is explicit).`,
        }),
      )
    )
      return;
    setBusyId(doc.id);
    setError(null);
    try {
      await api.deleteProposal(doc.id, null);
      setTrash((cur) => [doc, ...cur.filter((d) => d.id !== doc.id)]);
      if (shareId === doc.id) {
        setShareId(null);
        setPanel(null);
      }
      await load();
    } catch (err) {
      fail(messageOf(err, t({ ko: '문서를 휴지통으로 옮기지 못했습니다.', en: 'Could not move the document to trash.' })));
    } finally {
      setBusyId(null);
    }
  }

  async function restoreDoc(doc: DocRow): Promise<void> {
    setBusyId(doc.id);
    setError(null);
    try {
      await restoreProposalReq(doc.id);
      setTrash((cur) => cur.filter((d) => d.id !== doc.id));
      await load();
    } catch (err) {
      fail(messageOf(err, t({ ko: '문서를 복구하지 못했습니다.', en: 'Could not restore the document.' })));
    } finally {
      setBusyId(null);
    }
  }

  const uploading = upload !== null;

  return (
    <div
      style={{ ...S.wrap, ...(dragging ? S.wrapDrag : {}) }}
      data-testid="folder-docs"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div style={S.bar}>
        <span style={S.count}>{t({ ko: '문서', en: 'Documents' })} {docs.length}</span>
        {canEdit ? (
          <div style={S.barRight}>
            <span style={S.hint}>{t({ ko: '파일을 끌어다 놓아도 업로드됩니다', en: 'You can also drag and drop files to upload' })}</span>
            <button
              type="button"
              style={{ ...S.actionBtn, ...(trashOpen ? S.actionBtnOn : {}) }}
              aria-expanded={trashOpen}
              data-testid="folder-docs-trash-toggle"
              onClick={() => setTrashOpen((v) => !v)}
            >
              {t({ ko: '휴지통', en: 'Trash' })} {trash.length}
            </button>
            <button
              type="button"
              style={{ ...S.addBtn, ...(uploading ? S.addBtnOff : {}) }}
              disabled={uploading}
              data-testid="folder-docs-upload"
              onClick={() => fileRef.current?.click()}
            >
              {upload
                ? t({ ko: `업로드 중… ${upload.done + 1}/${upload.total}`, en: `Uploading… ${upload.done + 1}/${upload.total}` })
                : t({ ko: '＋ 문서 추가', en: '＋ Add document' })}
            </button>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept={ACCEPT}
              style={{ display: 'none' }}
              disabled={uploading}
              aria-label={t({ ko: '문서 파일 선택', en: 'Select document file' })}
              data-testid="folder-docs-file-input"
              onChange={onPick}
            />
          </div>
        ) : (
          <span style={S.hint} data-testid="folder-docs-readonly">
            {t({ ko: '보기 권한 — 읽기 전용', en: 'Viewer access — read only' })}
          </span>
        )}
      </div>

      {error ? (
        <div style={S.error} role="alert" data-testid="folder-docs-error">
          {error}
        </div>
      ) : null}

      {canEdit && trashOpen ? (
        <div style={S.trashBox} data-testid="folder-docs-trash">
          {/* 서버 목록 EP 는 deletedAt IS NULL 고정 — 삭제 문서 열거가 아직 없다. 있는 척하지 않고
              이번 화면에서 옮긴 것만 보여준다. 복구 EP 는 실재하므로 여기 복구는 진짜다. */}
          <p style={S.trashNote}>
            {t({
              ko: '이번 화면에서 휴지통으로 보낸 문서만 표시됩니다 — 서버의 휴지통 목록 조회가 아직 없어 이전에 삭제한 문서는 여기 안 보입니다(서버에는 보존돼 있어 복구 가능합니다). 복구된 문서의 공유 링크는 중지 상태로 남습니다 — 재공유는 “공유”에서 명시적으로.',
              en: 'Only documents sent to trash in this screen are shown — the server has no trash listing yet, so documents deleted earlier don’t appear here (they’re still preserved on the server and can be restored). A restored document’s share links stay stopped — re-share explicitly in “Share”.',
            })}
          </p>
          {trash.length === 0 ? (
            <div style={S.trashEmpty} data-testid="folder-docs-trash-empty">
              {t({ ko: '휴지통으로 보낸 문서가 없습니다.', en: 'No documents sent to trash.' })}
            </div>
          ) : (
            trash.map((doc) => (
              <div key={doc.id} style={S.trashRow} data-testid={`trash-doc-${doc.id}`}>
                <span style={S.badge}>{KIND_LABEL[doc.kind] ?? String(doc.kind).toUpperCase()}</span>
                <span style={S.title} title={doc.title}>
                  {doc.title}
                </span>
                <span style={S.spacer} />
                <button
                  type="button"
                  style={S.actionBtn}
                  disabled={busyId === doc.id}
                  data-testid={`trash-restore-${doc.id}`}
                  onClick={() => void restoreDoc(doc)}
                >
                  {busyId === doc.id ? t({ ko: '복구 중…', en: 'Restoring…' }) : t({ ko: '복구', en: 'Restore' })}
                </button>
              </div>
            ))
          )}
        </div>
      ) : null}

      {dragging && canEdit ? (
        <div style={S.dropHint}>
          {t({ ko: '여기에 놓으면 업로드됩니다 (HTML·MD·PDF·PPTX, 50MB 이하)', en: 'Drop here to upload (HTML, MD, PDF, PPTX, 50MB or less)' })}
        </div>
      ) : null}

      <div style={S.table}>
        {loading && docs.length === 0 ? (
          <div style={S.empty}>{t({ ko: '불러오는 중…', en: 'Loading…' })}</div>
        ) : docs.length === 0 ? (
          <div style={S.empty} data-testid="folder-docs-empty">
            {canEdit
              ? t({ ko: '문서가 없습니다 — 파일을 끌어다 놓거나 “문서 추가”로 올리세요', en: 'No documents — drag and drop files or use “Add document” to upload' })
              : t({ ko: '문서가 없습니다', en: 'No documents' })}
          </div>
        ) : (
          docs.map((doc) => {
            const busy = busyId === doc.id;
            const shareOpen = shareId === doc.id;
            return (
              <div key={doc.id} style={S.rowGroup}>
              <div style={S.row} data-testid={`folder-doc-${doc.id}`}>
                <span style={S.badge}>{KIND_LABEL[doc.kind] ?? String(doc.kind).toUpperCase()}</span>

                {editingId === doc.id ? (
                  <input
                    style={S.titleInput}
                    value={editTitle}
                    autoFocus
                    disabled={busy}
                    aria-label={t({ ko: '문서 제목', en: 'Document title' })}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void saveTitle(doc);
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                  />
                ) : (
                  <span style={S.title} title={doc.title}>
                    {doc.title}
                  </span>
                )}

                {/* 서버는 VIEWER 에게 shareUrl 을 마스킹한다(EDITOR+ 만 노출) — 그 상태에서
                    '내부 전용' 이라 단정하면 실제로 공개된 문서를 안전하다고 오표기하게 된다.
                    값이 없는 것과 볼 권한이 없는 것을 구분한다(externalRef 마스킹과 같은 관용구). */}
                {!canEdit ? (
                  <span style={S.privatePill} data-testid={`folder-doc-state-${doc.id}`}>
                    {t({ ko: '공유 상태 — 표시 권한 없음', en: 'Share status — you can’t view it' })}
                  </span>
                ) : doc.shareUrl ? (
                  <span style={S.sharePill} data-testid={`folder-doc-state-${doc.id}`}>
                    {t({ ko: '공유중', en: 'Shared' })}
                  </span>
                ) : (
                  <span style={S.privatePill} data-testid={`folder-doc-state-${doc.id}`}>
                    {t({ ko: '내부 전용', en: 'Internal only' })}
                  </span>
                )}
                <span style={S.spacer} />
                <span style={S.meta}>{formatSize(doc.fileSize)}</span>
                <span style={S.date}>{formatDate(doc.createdAt)}</span>

                <div style={S.actions}>
                  {editingId === doc.id ? (
                    <>
                      <button type="button" style={S.actionBtn} disabled={busy} onClick={() => void saveTitle(doc)}>
                        {busy ? t({ ko: '저장 중…', en: 'Saving…' }) : t({ ko: '저장', en: 'Save' })}
                      </button>
                      <button type="button" style={S.actionBtn} disabled={busy} onClick={() => setEditingId(null)}>
                        {t({ ko: '취소', en: 'Cancel' })}
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" style={S.actionBtn} onClick={() => openDoc(doc)}>
                        {t({ ko: '열기', en: 'Open' })}
                      </button>
                      {canEdit ? (
                        <>
                          {/* 열람 분석 — 세션 신원 경로(/dashboard/:id, key 없음). ownerKey 는 관리
                              크레덴셜이라 URL 에 싣지 않는다. 요약 EP 는 폴더 EDITOR+ 세션을 허용한다. */}
                          <Link
                            to={`/dashboard/${doc.id}`}
                            style={S.actionLink}
                            data-testid={`folder-doc-analytics-${doc.id}`}
                          >
                            {t({ ko: '열람 분석', en: 'Read analytics' })}
                          </Link>
                          <button
                            type="button"
                            style={{ ...S.actionBtn, ...(shareOpen ? S.actionBtnOn : {}) }}
                            disabled={busy}
                            aria-expanded={shareOpen}
                            data-testid={`folder-doc-share-toggle-${doc.id}`}
                            onClick={() => toggleShare(doc)}
                          >
                            {t({ ko: '공유', en: 'Share' })}
                          </button>
                          <button type="button" style={S.actionBtn} disabled={busy} onClick={() => startRename(doc)}>
                            {t({ ko: '이름 변경', en: 'Rename' })}
                          </button>
                          <button
                            type="button"
                            style={{ ...S.actionBtn, ...S.danger }}
                            disabled={busy}
                            onClick={() => void removeDoc(doc)}
                          >
                            {t({ ko: '휴지통으로 이동', en: 'Move to trash' })}
                          </button>
                        </>
                      ) : null}
                    </>
                  )}
                </div>
              </div>

              {canEdit && shareOpen ? (
                <div style={S.sharePanel} data-testid={`folder-doc-share-${doc.id}`}>
                  {!panel || panel.docId !== doc.id || panel.loading ? (
                    <span style={S.shareNote}>{t({ ko: '링크 상태를 불러오는 중…', en: 'Loading link status…' })}</span>
                  ) : panel.error ? (
                    <span style={S.shareWarn} role="alert">
                      {panel.error}
                    </span>
                  ) : panel.links.length === 0 ? (
                    <div style={S.linkRow}>
                      <div style={S.shareCol}>
                        <span style={S.shareWarn}>{t({ ko: '링크를 아는 사람은 로그인 없이 열람합니다.', en: 'Anyone with the link can view it without signing in.' })}</span>
                        <span style={S.shareNote}>
                          {t({ ko: '지금은 내부 전용입니다 — 자료함 접근권한이 있는 멤버만 볼 수 있습니다.', en: 'This is currently internal only — only members with access to this folder can view it.' })}
                        </span>
                      </div>
                      <span style={S.spacer} />
                      <button
                        type="button"
                        style={S.actionBtn}
                        disabled={busy}
                        data-testid={`folder-doc-share-create-${doc.id}`}
                        onClick={() => void createShareLink(doc)}
                      >
                        {busy ? t({ ko: '만드는 중…', en: 'Creating…' }) : t({ ko: '공유 링크 만들기', en: 'Create share link' })}
                      </button>
                    </div>
                  ) : (
                    <>
                      <span style={S.shareWarn}>
                        {t({
                          ko: '링크를 아는 사람은 로그인 없이 열람합니다. 잘못 보낸 링크는 “중지”로 즉시 차단됩니다 — 열람 통계는 남고, 언제든 재개할 수 있습니다.',
                          en: 'Anyone with the link can view it without signing in. A link sent by mistake can be blocked immediately with “Stop” — view stats are kept and you can resume anytime.',
                        })}
                      </span>
                      {panel.links.map((link) => {
                        const linkBusy = linkBusyId === link.id;
                        return (
                          <div key={link.id} style={S.linkBox} data-testid={`share-link-${link.id}`}>
                            <div style={S.linkRow}>
                              <span style={S.shareLabel} title={link.alias}>
                                {link.alias}
                              </span>
                              {link.disabled ? (
                                <span style={S.stoppedPill} data-testid={`share-link-state-${link.id}`}>
                                  {t({ ko: '중지됨', en: 'Stopped' })}
                                </span>
                              ) : (
                                <span style={S.sharePill} data-testid={`share-link-state-${link.id}`}>
                                  {t({ ko: '활성', en: 'Active' })}
                                </span>
                              )}
                              <input
                                readOnly
                                style={S.shareInput}
                                value={link.shareUrl}
                                aria-label={t({ ko: `${link.alias} 공유 링크`, en: `${link.alias} share link` })}
                                data-testid={`share-link-url-${link.id}`}
                                onFocus={(e) => e.currentTarget.select()}
                              />
                              <button
                                type="button"
                                style={S.actionBtn}
                                data-testid={`share-link-copy-${link.id}`}
                                onClick={() => void copyLinkUrl(link)}
                              >
                                {copiedId === link.id ? t({ ko: '복사됨', en: 'Copied' }) : t({ ko: '복사', en: 'Copy' })}
                              </button>
                              <button
                                type="button"
                                // 두 분기 모두 borderColor/color 를 명시 — border 축약형과 섞인 속성이
                                // 리렌더에서 제거되면 React 가 스타일 충돌을 경고한다(재개↔중지 토글 버튼).
                                style={{ ...S.actionBtn, ...(link.disabled ? S.dangerOff : S.danger) }}
                                disabled={linkBusy}
                                data-testid={`share-link-toggle-${link.id}`}
                                onClick={() => void toggleLinkDisabled(doc, link)}
                              >
                                {linkBusy
                                  ? t({ ko: '적용 중…', en: 'Applying…' })
                                  : link.disabled
                                    ? t({ ko: '재개', en: 'Resume' })
                                    : t({ ko: '중지', en: 'Stop' })}
                              </button>
                            </div>
                            <div style={S.linkRow}>
                              <label style={S.shareNote}>
                                {t({ ko: '만료일', en: 'Expires' })}{' '}
                                <input
                                  type="date"
                                  style={S.dateInput}
                                  value={expiryDraft[link.id] ?? toDateInput(link.expiresAt)}
                                  disabled={linkBusy}
                                  aria-label={t({ ko: `${link.alias} 만료일`, en: `${link.alias} expiry date` })}
                                  data-testid={`share-link-expiry-${link.id}`}
                                  onChange={(e) =>
                                    setExpiryDraft((cur) => ({ ...cur, [link.id]: e.target.value }))
                                  }
                                />
                              </label>
                              <button
                                type="button"
                                style={S.actionBtn}
                                disabled={linkBusy || expiryDraft[link.id] === undefined}
                                data-testid={`share-link-expiry-apply-${link.id}`}
                                onClick={() => void applyExpiry(doc, link)}
                              >
                                {t({ ko: '만료 적용', en: 'Apply expiry' })}
                              </button>
                              <span style={S.shareNote} data-testid={`share-link-expiry-now-${link.id}`}>
                                {link.expiresAt
                                  ? t({ ko: `현재 만료 ${formatDate(link.expiresAt)}`, en: `Currently expires ${formatDate(link.expiresAt)}` })
                                  : t({ ko: '현재 만료 없음', en: 'Currently no expiry' })}
                                {/* 빈 값 저장 = 무만료 요청 — 플랜 상한이 있으면 서버가 캡한 값이 그대로 표시된다 */}
                              </span>
                              <label style={S.shareNote}>
                                <input
                                  type="checkbox"
                                  checked={link.allowDownload}
                                  disabled={linkBusy}
                                  data-testid={`share-link-download-${link.id}`}
                                  onChange={(e) => void patchLink(doc, link, { allowDownload: e.target.checked })}
                                />{' '}
                                {t({ ko: '다운로드 허용', en: 'Allow download' })}
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              ) : null}
              </div>
            );
          })
        )}
      </div>

      <p style={S.foot}>
        {t({
          ko: '“열기”는 새 탭에서 문서를 엽니다 — HTML·MD 는 바로 보이고, PDF·PPTX 는 브라우저 설정에 따라 내려받아집니다.',
          en: '“Open” opens the document in a new tab — HTML and MD show right away, while PDF and PPTX may download depending on your browser settings.',
        })}
        {canEdit
          ? ' ' +
            t({
              ko: '올린 문서는 내부 전용입니다 — 자료함 접근권한이 통치하며, 공개 링크는 “공유”에서 문서별로 직접 만들 때만 생깁니다. 삭제는 휴지통 이동이라 복구할 수 있습니다.',
              en: 'Uploaded documents are internal only — folder access governs them, and a public link only exists when you create one per document in “Share”. Deleting moves it to trash so it can be restored.',
            })
          : null}
      </p>
    </div>
  );
}

const S: Record<string, CSSProperties> = {
  wrap: { border: '1px solid transparent', borderRadius: 12, transition: 'border-color .12s ease' },
  wrapDrag: { borderColor: 'var(--color-accent-ring)', background: 'var(--color-accent-subtle)' },
  bar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '18px 0 8px' },
  barRight: { display: 'flex', alignItems: 'center', gap: 10 },
  count: { fontSize: 13, fontWeight: 600, color: 'var(--color-muted)' },
  hint: { fontSize: 11.5, color: 'var(--color-subtle)' },
  addBtn: {
    fontSize: 12.5, padding: '6px 12px', borderRadius: 7, border: 'none',
    background: 'var(--color-accent)', color: '#fff', cursor: 'pointer',
  },
  addBtnOff: { background: 'var(--color-border-strong)', cursor: 'progress' },
  error: {
    whiteSpace: 'pre-wrap', fontSize: 12.5, lineHeight: 1.6, color: 'var(--color-danger)',
    background: 'var(--color-danger-subtle)', border: '1px solid var(--color-danger)',
    borderRadius: 8, padding: '8px 12px', margin: '0 0 8px',
  },
  dropHint: {
    fontSize: 12.5, color: 'var(--color-accent-active)', background: 'var(--color-accent-subtle)',
    border: '1px dashed var(--color-accent-ring)', borderRadius: 8, padding: '10px 12px', margin: '0 0 8px', textAlign: 'center',
  },
  table: { border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden' },
  empty: { color: 'var(--color-subtle)', fontSize: 13.5, padding: '48px 16px', textAlign: 'center', background: 'var(--color-card)' },
  rowGroup: { display: 'block' },
  row: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
    borderBottom: '1px solid var(--color-border)', background: 'var(--color-card)',
  },
  badge: {
    fontSize: 10.5, fontWeight: 700, color: 'var(--color-muted)', background: 'var(--surface-sunken)',
    borderRadius: 5, padding: '2px 6px', minWidth: 40, textAlign: 'center', flexShrink: 0,
  },
  title: {
    fontSize: 13.5, color: 'var(--color-fg)', fontWeight: 500,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 380,
  },
  titleInput: {
    flex: 1, minWidth: 160, maxWidth: 420, fontSize: 13.5, padding: '5px 9px', borderRadius: 7,
    border: '1px solid var(--color-border-strong)', background: 'var(--color-card)', color: 'var(--color-fg)',
  },
  sharePill: {
    fontSize: 10.5, fontWeight: 600, color: 'var(--color-ok)', background: 'var(--color-ok-subtle)',
    borderRadius: 999, padding: '2px 8px', flexShrink: 0,
  },
  privatePill: {
    fontSize: 10.5, fontWeight: 600, color: 'var(--color-subtle)', background: 'var(--surface-sunken)',
    borderRadius: 999, padding: '2px 8px', flexShrink: 0,
  },
  stoppedPill: {
    fontSize: 10.5, fontWeight: 700, color: 'var(--color-danger)', background: 'var(--color-danger-subtle)',
    borderRadius: 999, padding: '2px 8px', flexShrink: 0,
  },
  sharePanel: {
    display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 14px',
    borderBottom: '1px solid var(--color-border)', background: 'var(--surface-sunken)',
  },
  shareCol: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  linkBox: {
    display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid var(--color-border)',
    borderRadius: 8, padding: '8px 10px', background: 'var(--color-card)',
  },
  linkRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  shareLabel: {
    fontSize: 11.5, fontWeight: 600, color: 'var(--color-muted)', flexShrink: 0,
    maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  shareInput: {
    flex: 1, minWidth: 160, fontSize: 12.5, padding: '5px 9px', borderRadius: 7,
    border: '1px solid var(--color-border-strong)', background: 'var(--color-card)', color: 'var(--color-fg)',
  },
  dateInput: {
    fontSize: 12, padding: '4px 8px', borderRadius: 7,
    border: '1px solid var(--color-border-strong)', background: 'var(--color-card)', color: 'var(--color-fg)',
  },
  shareWarn: { fontSize: 11.5, color: 'var(--color-muted)', lineHeight: 1.5 },
  shareNote: { fontSize: 11.5, color: 'var(--color-subtle)', lineHeight: 1.5 },
  trashBox: {
    border: '1px dashed var(--color-border-strong)', borderRadius: 10, padding: '10px 12px',
    margin: '0 0 8px', background: 'var(--surface-sunken)', display: 'flex', flexDirection: 'column', gap: 8,
  },
  trashNote: { fontSize: 11.5, color: 'var(--color-subtle)', margin: 0, lineHeight: 1.6 },
  trashEmpty: { fontSize: 12.5, color: 'var(--color-subtle)' },
  trashRow: { display: 'flex', alignItems: 'center', gap: 12 },
  spacer: { flex: 1 },
  meta: { fontSize: 12, color: 'var(--color-subtle)', width: 72, textAlign: 'right', flexShrink: 0 },
  date: { fontSize: 12, color: 'var(--color-subtle)', width: 84, textAlign: 'right', flexShrink: 0 },
  actions: { display: 'flex', gap: 6, flexShrink: 0 },
  actionBtn: {
    fontSize: 12, padding: '5px 10px', borderRadius: 7, border: '1px solid var(--color-border-strong)',
    background: 'var(--color-card)', color: 'var(--color-fg)', cursor: 'pointer',
  },
  actionLink: {
    fontSize: 12, padding: '5px 10px', borderRadius: 7, border: '1px solid var(--color-border-strong)',
    background: 'var(--color-card)', color: 'var(--color-fg)', textDecoration: 'none',
  },
  actionBtnOn: { borderColor: 'var(--color-accent-ring)', background: 'var(--color-accent-subtle)', color: 'var(--color-accent-active)' },
  danger: { color: 'var(--color-danger)', borderColor: 'var(--color-danger)' },
  /** danger 와 짝 — 토글되는 버튼은 두 상태 모두 같은 속성 집합을 명시해야 한다(위 주석 참조). */
  dangerOff: { color: 'var(--color-fg)', borderColor: 'var(--color-border-strong)' },
  foot: { fontSize: 11.5, color: 'var(--color-subtle)', margin: '8px 2px 0', lineHeight: 1.6 },
};
